/**
 * Synthesise per-matchday standings history for the AI League chart.
 *
 * The AI League is not a Firestore league — there is no leagues/{id}/history/*.
 * This helper produces LeagueHistoryColumn[] in the same shape as settlement writes
 * so buildStandingsSeries and StandingsChartPanel can be reused unchanged.
 */
import type { AiModelContent } from '@/constants/aiLeague/aiLeagueTypes';
import { computeListScore, type ComputeListScoreOptions } from '@/helpers/scoring/computeListScore';
import type { Country } from '@/models/country';
import type { gameResult } from '@/models/gameUpdate';
import type { LeagueHistoryColumn } from '@/models/leagueHistory';
import type { ProgressionAward } from '@/models/scoring';

export interface AiLeagueChartUser {
  id: 'you';
  name: string;
  countryList: Country[];
  listIndexSwapPair?: [number, number] | null;
  mdReset?: number;
}

export interface BuildAiLeagueHistoryParams {
  gameResults: gameResult[];
  throughMatchday: number;
  aiModels: AiModelContent[];
  user?: AiLeagueChartUser;
  /** Tournament scoring inputs — must match settlement (progression awards + config). */
  progressionAwards?: ProgressionAward[];
  matchdayCount?: number;
  swapMatchday?: number;
}

interface ScoredParticipant {
  id: string;
  score: number;
}

function baseScoringOptions(params: BuildAiLeagueHistoryParams): Omit<ComputeListScoreOptions, 'atMatchday'> {
  return {
    progressionAwards: params.progressionAwards ?? [],
    matchdayCount: params.matchdayCount,
    swapMatchday: params.swapMatchday,
  };
}

function scoreParticipant(
  params: BuildAiLeagueHistoryParams,
  matchday: number,
  list: Country[],
  options?: { listIndexSwapPair?: [number, number] | null; mdReset?: number },
): number {
  if (list.length !== 10) return 0;
  return computeListScore(list, params.gameResults, {
    ...baseScoringOptions(params),
    atMatchday: matchday,
    listIndexSwapPair: options?.listIndexSwapPair ?? null,
    mdReset: options?.mdReset ?? 0,
  });
}

function scoreAtMatchday(
  params: BuildAiLeagueHistoryParams,
  matchday: number,
): ScoredParticipant[] {
  const rows: ScoredParticipant[] = params.aiModels.map((model) => ({
    id: model.id,
    score: model.listValid
      ? scoreParticipant(params, matchday, model.countryList, {
          listIndexSwapPair: model.listIndexSwapPair,
        })
      : 0,
  }));

  if (params.user) {
    rows.push({
      id: params.user.id,
      score: scoreParticipant(params, matchday, params.user.countryList, {
        listIndexSwapPair: params.user.listIndexSwapPair,
        mdReset: params.user.mdReset,
      }),
    });
  }

  rows.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  return rows;
}

/** Build settled matchday columns 1..throughMatchday for the AI League standings chart. */
export function buildAiLeagueHistoryColumns(params: BuildAiLeagueHistoryParams): LeagueHistoryColumn[] {
  const { throughMatchday } = params;
  if (throughMatchday < 1) return [];

  const columns: LeagueHistoryColumn[] = [];
  for (let matchday = 1; matchday <= throughMatchday; matchday += 1) {
    const ranked = scoreAtMatchday(params, matchday);
    columns.push({
      matchday,
      order: ranked.map((row) => row.id),
      scores: ranked.map((row) => row.score),
    });
  }
  return columns;
}
